import React, { memo, useCallback, useEffect, useState } from 'react';

import { Button, Col, Form, Input, Radio, Row, Select, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import axios from 'axios';

import FloatingLabel from '../FloatingLabel/FloatingLabel';
import SelectDropdown from '../SelectDropdown/SelectDropdown';
import EmailField from '../SharedComponents/FormComponents/EmailField';

import { CheckCircle, ChevronDown, ChevronUp } from '../../assets/icons';
import { toastNotify } from '../../utils/toastNotify';

import { getAllPropertyTypes } from '../../services/FiltersApi/filters';
import { addSellPropertyService } from '../../services/PropertyDetailsAPI';

import useIsComponentMounted from '../../Hooks/useIsComponentMounted';
import useMediaQuery from '../../Hooks/useMediaQuery';
import { FIRST_NAME_LENGTHS, FIRST_NAME_VALIDATION, LAST_NAME_LENGTHS } from '../../utils/validationMessages';
import { alphabetSpaceRegex } from '../../utils/Validations';

const AsyncSelectDropdown = memo(({ label, name, inputId, id, onChangeSelect, rules }) => {
  const isComponentMounted = useIsComponentMounted();
  const [typeOpen, setTypeOpen] = useState(false);

  const [isFetchingTypes, setFetchingTypes] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const [options, setOptions] = useState([]);

  const fetchPropertyTypes = useCallback(
    async (cancelToken) => {
      try {
        if (!isComponentMounted.current) return;
        setFetchingTypes(true);
        const response = await getAllPropertyTypes(cancelToken);
        setOptions([...(response.data?.data?.propertyTypes ?? [])]);
      } catch (error) {
        if (error?.code === 'ERR_CANCELED') return;
        toastNotify('error', error?.response?.data?.message);
      } finally {
        isComponentMounted.current && setFetchingTypes(false);
      }
    },
    [isComponentMounted?.current]
  );

  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    fetchPropertyTypes(source.token);

    return () => source.cancel('Component got unmounted');
  }, []);

  return (
    <FloatingLabel label={label} inputId={inputId} inputValue={selectedValue} className='w-100'>
      <Form.Item className='mb-24' name={name} rules={rules}>
        <Select
          id={id}
          popupClassName='filter-select-dropdown'
          onChange={(value) => {
            setSelectedValue(value);
            onChangeSelect(name, value);
          }}
          getPopupContainer={(trigger) => trigger.parentNode}
          value={selectedValue}
          onDropdownVisibleChange={() => setTypeOpen(!typeOpen)}
          suffixIcon={typeOpen ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
          menuItemSelectedIcon={<CheckCircle verticalAlign='-8px' color='#8A4743' />}
          loading={isFetchingTypes}
        >
          {options?.map((option) => (
            <Select.Option key={option?.propertyTypeId} value={option?.propertyTypeId}>
              {option?.name}
            </Select.Option>
          ))}
          <Select.Option key={'other'} value={'other'}>
            Other
          </Select.Option>
        </Select>
      </Form.Item>
    </FloatingLabel>
  );
});

const SellPropertyForm = ({ onSubmitSuccess }) => {
  const screenMD = useMediaQuery('(min-width: 1000px)');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form] = useForm();

  const [formValid, setFormValid] = useState(false);

  const onSubmitForm = async (values) => {
    try {
      setIsSubmitting(true);
      const response = await addSellPropertyService({
        ...values,
        propertyType: String(values?.propertyType),
      });
      if (!response.data?.data?.isError) {
        onSubmitSuccess();
      } else {
        toastNotify('error', response?.data?.message);
      }
    } catch (error) {
      toastNotify('error', error?.response?.data?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const askingPrices = ['under $250,000', '$250,000 - $500,000', '$500,000 - $750,000', '$750,000 - $1,000,000', '$1,000,000+'];
  const durations = ['As soon as possible', 'Less than a month', '2-3 months', '4 months or more'];

  const handleSelectChange = (inputName, inputValue) => {
    form.setFieldValue({ [inputName]: inputValue });
  };

  const onHandlePaste = (e) => {
    const pastedData = e.clipboardData.getData('text/plain').trim();
    handleSelectChange('phoneNumber', pastedData.replace(/[^0-9]/g, ''));
  };

  return (
    <>
      {screenMD && <h4 className='text-center mb-24'>Sell your property</h4>}
      <div className='sell-property-form'>
        <Form
          form={form}
          requiredMark
          onFinish={onSubmitForm}
          name='sell-property-form'
          initialValues={{
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            propertyAddress: '',
            propertyType: '',
            askingPrice: '',
            duration: '',
          }}
          onFinishFailed={() => { }}
          onFieldsChange={() => setFormValid(form.getFieldsError().filter(({ errors }) => errors.length).length !== 0)}
          onValuesChange={() => setFormValid(form.getFieldsError().filter(({ errors }) => errors.length).length !== 0)}
          layout='vertical'
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <FloatingLabel inputValue={Form.useWatch('firstName', form)} label='First name' inputId='name'>
                <Form.Item
                  validateTrigger="onBlur"
                  className='mb-24'
                  name='firstName'
                  rules={[
                    { required: true, whitespace: true, message: FIRST_NAME_VALIDATION.REQUIRED },
                    {
                      transform: (val) => val?.trim(),
                      max: 35,
                      message: `Value should be less than 35 character`,
                    },
                    {
                      transform: (val) => val?.trim(),
                      min: FIRST_NAME_LENGTHS.MIN,
                      message: `Must be at least ${FIRST_NAME_LENGTHS.MIN} characters`,
                    },
                    {
                      pattern: alphabetSpaceRegex,
                      message: FIRST_NAME_VALIDATION.FORMAT,
                    }
                  ]}
                >
                  <Input id='firstName' />
                </Form.Item>
              </FloatingLabel>
            </Col>
            <Col xs={24} md={12}>
              <FloatingLabel inputValue={Form.useWatch('lastName', form)} label='Last name' placeholder='$' inputId='name'>
                <Form.Item
                  validateTrigger="onBlur"
                  className='mb-24'
                  name='lastName'
                  rules={[
                    { required: true, whitespace: true, message: 'Please enter Last Name' },
                    {
                      max: 35,
                      message: `Value should be less than 35 character`,
                    },
                    {
                      min: LAST_NAME_LENGTHS.MIN,
                      message: `Must be at least ${LAST_NAME_LENGTHS.MIN} characters`,
                    },
                  ]}
                >
                  <Input id='lastName' />
                </Form.Item>
              </FloatingLabel>
            </Col>
            <Col span={24}>
              <FloatingLabel inputValue={Form.useWatch('email', form)} label='Email' inputId='email'>
                <EmailField email={Form.useWatch('email', form)} setEmail={() => {}} />
              </FloatingLabel>
            </Col>
            <Col span={24}>
              <FloatingLabel inputValue={Form.useWatch('phoneNumber', form)} label='Phone #' inputId='phone'>
                <Form.Item
                  validateTrigger="onBlur"
                  className='mb-24'
                  name='phoneNumber'
                  rules={[
                    {
                      transform: (val) => val?.replace(/[^0-9]/g, ''),
                      pattern: /^(1|)?(\d{3})(\d{3})(\d{4})$/,
                      required: true,
                      message: 'Please enter a valid phone number',
                    },
                    {
                      max: 11,
                      message: 'Value should be less than 11 character',
                    },
                    {
                      min: 9,
                      message: 'Must be at least 9 characters',
                    },
                  ]}
                  shouldUpdate
                >
                  <Input
                    type='number'
                    id='phoneNumber'
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onPaste={onHandlePaste}
                    onWheel={(e) => {
                      e.target.blur();
                    }}
                    controls={false}
                  />
                </Form.Item>
              </FloatingLabel>
            </Col>
            <Col span={24}>
              <FloatingLabel inputValue={Form.useWatch('propertyAddress', form)} label='Property Address' inputId='address'>
                <Form.Item
                  validateTrigger="onBlur"
                  className='mb-24'
                  name='propertyAddress'
                  rules={[
                    { required: true, whitespace: true, message: 'Please enter Property Address.' },
                    {
                      max: 250,
                      message: 'Value should be less than 250 character',
                    },
                    {
                      min: 2,
                      message: 'Must be at least 5 characters',
                    },
                  ]}
                >
                  <Input id='propertyAddress' autoComplete='off' />
                </Form.Item>
              </FloatingLabel>
            </Col>
            <Col span={24}>
              <AsyncSelectDropdown
                id={'type'}
                inputId={'type'}
                label={'Property type'}
                name={'propertyType'}
                onChangeSelect={handleSelectChange}
                rules={[{ required: true, message: 'Please pick a Property Type.' }]}
              />
            </Col>
            <Col span={24}>
              <SelectDropdown
                id={'price'}
                inputId={'price'}
                label={'Asking price'}
                name={'askingPrice'}
                options={askingPrices}
                onChangeSelect={handleSelectChange}
                rules={[{ required: true, message: 'Please pick an asking price.' }]}
              />
            </Col>
            <Col span={24}>
              <SelectDropdown
                id={'sell'}
                inputId={'sell'}
                label={'When are you looking to sell?'}
                name={'duration'}
                options={durations}
                onChangeSelect={handleSelectChange}
                rules={[{ required: true, message: 'Please pick a duration.' }]}
              />
            </Col>
          </Row>
          <p className='mb-0'>Is your property currently occupied?</p>
          <Form.Item name='isOccupied' className='mb-48' rules={[{ required: true, message: 'Please select property status.' }]}>
            <Radio.Group className='vertical-radio'>
              <Space direction='vertical' size={18}>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item className='mb-0' shouldUpdate>
            {screenMD ? (
              <Button type='primary' className='w-100' htmlType='submit' loading={isSubmitting} disabled={formValid}>
                Submit
              </Button>
            ) : (
              <Button type='primary' className='w-100' htmlType='submit' loading={isSubmitting} disabled={formValid}>
                Submit
              </Button>
            )}
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default SellPropertyForm;
